// ══════════════════════════════════════════════════════════════════════════════
// PAGE DÉTAIL GROUPE - FEED STYLE WORKPLACE BY META
// Posts, photos, vidéos, sondages, événements, réactions, commentaires
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Users, Settings, UserPlus, Link2, Copy, Check,
  MessageSquare, Loader2, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { WorkplaceFeed } from '../../components/feed';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GroupDetailPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('feed');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token');

  useEffect(() => {
    fetchGroup();
    fetchCurrentUser();
  }, [groupId]);

  const fetchCurrentUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/buyer/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchGroup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGroup(data);
      } else {
        toast.error('Groupe non trouvé');
        navigate('/cockpit-particulier/groupes');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/join-group/${group.invite_code}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Groupe non trouvé</p>
      </div>
    );
  }

  const groupColor = group.type === 'famille' ? '#EC4899' : group.type === 'amis' ? '#8B5CF6' : '#06B6D4';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/cockpit-particulier/groupes')}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{group.emoji || '👥'}</span>
                  <h1 className="text-xl font-bold text-gray-900">{group.name}</h1>
                </div>
                <p className="text-sm text-gray-500">
                  {group.members?.length || 0} membre{(group.members?.length || 0) > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowInviteModal(true)}
                className="flex items-center gap-2"
              >
                <UserPlus size={18} />
                Inviter
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/cockpit-particulier/groupes/${groupId}/tchat`)}
                className="flex items-center gap-2"
              >
                <MessageSquare size={18} />
                Tchat
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-6">
            {[
              { id: 'feed', label: 'Publications' },
              { id: 'members', label: 'Membres' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id 
                    ? 'border-current text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                style={activeTab === tab.id ? { color: groupColor } : {}}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {activeTab === 'feed' && (
            <motion.div
              key="feed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <WorkplaceFeed
                contextType="group"
                contextId={groupId}
                token={token}
                currentUser={currentUser}
                members={group.members || []}
                accentColor={groupColor}
                emptyStateMessage="Aucune publication dans ce groupe"
              />
            </motion.div>
          )}

          {activeTab === 'members' && (
            <motion.div
              key="members"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
            >
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Membres ({group.members?.length || 0})</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {(group.members || []).map((member, index) => (
                  <div key={member.user_id || index} className="p-4 flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: groupColor }}
                    >
                      {(member.name || member.display_name)?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{member.name || member.display_name}</p>
                      <p className="text-sm text-gray-500">{member.role === 'admin' ? 'Admin' : 'Membre'}</p>
                    </div>
                  </div>
                ))}
                {(!group.members || group.members.length === 0) && (
                  <div className="p-8 text-center text-gray-500">
                    Aucun membre pour l'instant
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Inviter des membres</h3>
                <button onClick={() => setShowInviteModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500 mb-2">Lien d'invitation</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm truncate">
                      {window.location.origin}/join-group/{group.invite_code}
                    </div>
                    <Button onClick={copyInviteLink} variant="outline" className="shrink-0">
                      {copiedLink ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                    </Button>
                  </div>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500 mb-2">Code d'invitation</p>
                  <p className="text-2xl font-mono font-bold text-center tracking-wider" style={{ color: groupColor }}>
                    {group.invite_code}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
